<template>
  <div
    class="crm-column flex-shrink-0 w-72 flex flex-col rounded-xl bg-slate-100 dark:bg-slate-900"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
    :class="{ 'ring-2 ring-woot-400 ring-inset': isDragOver }"
  >
    <!-- Column Header -->
    <div class="flex items-center justify-between px-3 py-2.5">
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="size-2.5 rounded-full flex-shrink-0"
          :style="{ backgroundColor: stage.color }"
        />
        <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">
          {{ stage.name }}
        </h3>
        <span class="text-xs text-slate-500 bg-slate-200 dark:bg-slate-700 dark:text-slate-400 px-1.5 py-0.5 rounded-full">
          {{ cards.length }}
        </span>
      </div>
      <div class="flex items-center gap-1">
        <button
          class="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600"
          title="Adicionar card"
          @click="$emit('add-card', stage)"
        >
          <span class="i-lucide-plus size-4" />
        </button>
        <button
          class="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600"
          title="Editar coluna"
          @click="$emit('edit-stage', stage)"
        >
          <span class="i-lucide-settings-2 size-3.5" />
        </button>
      </div>
    </div>

    <!-- Total value -->
    <div v-if="totalValue > 0" class="px-3 pb-2">
      <span class="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
        Total: {{ formatCurrency(totalValue) }}
      </span>
    </div>

    <!-- Cards list -->
    <div
      class="flex-1 overflow-y-auto px-2 pb-2 space-y-2 min-h-16"
      style="max-height: calc(100vh - 280px);"
    >
      <KanbanCard
        v-for="card in cards"
        :key="card.id"
        :card="card"
        @click="$emit('card-click', card)"
        @edit="$emit('edit-card', card)"
        @delete="$emit('delete-card', card)"
        @dragstart="onCardDragStart"
        @dragend="onCardDragEnd"
      />

      <!-- Empty state -->
      <div
        v-if="cards.length === 0"
        class="flex flex-col items-center justify-center py-6 text-slate-400"
      >
        <span class="i-lucide-inbox size-8 mb-1 opacity-40" />
        <span class="text-xs">Nenhum card</span>
      </div>
    </div>

    <!-- Add card button at bottom -->
    <button
      class="mx-2 mb-2 py-1.5 flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
      @click="$emit('add-card', stage)"
    >
      <span class="i-lucide-plus size-3.5" />
      Adicionar card
    </button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  cards: { type: Array, default: () => [] },
});

const emit = defineEmits(['add-card', 'edit-stage', 'card-click', 'edit-card', 'delete-card', 'card-dropped']);

const isDragOver = ref(false);
let draggedCard = null;

const totalValue = computed(() =>
  props.cards.reduce((sum, c) => sum + parseFloat(c.deal_value || 0), 0)
);

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function onCardDragStart(card) {
  draggedCard = card;
}

function onCardDragEnd() {
  draggedCard = null;
}

function onDragOver() {
  isDragOver.value = true;
}

function onDragLeave() {
  isDragOver.value = false;
}

function onDrop(e) {
  isDragOver.value = false;
  const cardId = parseInt(e.dataTransfer.getData('cardId'));
  const fromStageId = parseInt(e.dataTransfer.getData('fromStageId'));
  if (cardId && fromStageId !== props.stage.id) {
    emit('card-dropped', {
      cardId,
      fromStageId,
      toStageId: props.stage.id,
      position: props.cards.length,
    });
  }
}
</script>
